import { type ComponentProps } from 'react'
import styled from 'styled-components'
// We need to import from 'next/link' here to define our component
// oxlint-disable-next-line no-restricted-imports
import BaseLink from 'next/link'
import { getIsPciRoutingRequired } from '@/features/shared/utils/getIsPciRoutingRequired'
import NProgress from 'nprogress'
import { isSamePathAndQuery } from '@/features/shared/utils/url'
import { useRouter as usePagesRouter } from 'next/compat/router'
import { getIsCrossRouterNavigation } from '@/features/shared/utils/appRouterNavigation'

export type NextLinkProps = Omit<
  ComponentProps<typeof BaseLink>,
  'role' | 'passHref' | 'as' | 'ref' | 'legacyBehavior' | 'href' | ' onNavigate'
> & { showUnderline?: boolean; href: string }

/**
 * # NextLink
 * @param children The component to cast as a link.
 * @param href The link to redirect the user to.
 *
 */
export const NextLink = ({
  children,
  showUnderline = false,
  ref,
  prefetch,
  ...restProps
}: NextLinkProps & { ref?: React.Ref<HTMLAnchorElement> }) => {
  const pagesRouter = usePagesRouter()
  const isCrossRouter = getIsCrossRouterNavigation({
    href: restProps.href,
    isPages: Boolean(pagesRouter),
  })
  return (
    <LinkWrapper
      $showUnderline={showUnderline}
      {...restProps}
      passHref
      ref={ref}
      onNavigate={(e) => {
        const { href } = restProps
        if (isSamePathAndQuery(href)) {
          e.preventDefault()
          return
        }
        if (isCrossRouter || getIsPciRoutingRequired({ href })) {
          e.preventDefault()
          // assigning href always pushes, so honor the link's replace prop
          if (restProps.replace) window.location.replace(href)
          else window.location.href = href
        }
        NProgress.start()
      }}
      // the other router's payload is unusable here, so fetching it is pure waste.
      // within a router next's own default applies.
      prefetch={isCrossRouter ? false : prefetch}
    >
      {children}
    </LinkWrapper>
  )
}

const LinkWrapper = styled(BaseLink)<{ $showUnderline: boolean }>`
  text-decoration: ${({ $showUnderline }) => ($showUnderline ? 'underline' : 'none')};
`
